/**
 * Gestionnaire du sélecteur de caméra
 */
export class CameraSelector {
    constructor(videoSourceManager) {
        this.videoSourceManager = videoSourceManager;
        this.isDropdownOpen = false;
        this.selectedDeviceId = '';
        this.availableDevices = [];
        
        this.init();
    }

    /**
     * Initialise le sélecteur de caméra
     */
    init() {
        this.button = document.getElementById('cameraSelectorButton');
        this.dropdown = document.getElementById('cameraDropdown');
        
        if (!this.button || !this.dropdown) {
            //consolewarn('⚠️ Éléments du sélecteur de caméra non trouvés');
            return;
        }

        // Event listeners
        this.button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleDropdown();
        });

        // Fermer le dropdown en cliquant ailleurs
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#cameraSelector')) {
                this.closeDropdown();
            }
        });

        // Charger les caméras disponibles
        this.loadAvailableCameras();
    }

    /**
     * Charge la liste des caméras disponibles
     */
    async loadAvailableCameras() {
        try {
            this.availableDevices = await this.videoSourceManager.getAvailableDevices();
            this.updateDropdownContent();
            //consolelog(`📷 ${this.availableDevices.length} caméra(s) détectée(s)`);
        } catch (error) {
            //consoleerror('Erreur lors du chargement des caméras:', error);
        }
    }

    /**
     * Met à jour le contenu du dropdown
     */
    updateDropdownContent() {
        this.dropdown.innerHTML = '';

        // Option par défaut
        const defaultOption = this.createCameraOption('', '📹 Caméra par défaut', true);
        this.dropdown.appendChild(defaultOption);

        // Options pour chaque caméra détectée
        this.availableDevices.forEach(device => {
            const option = this.createCameraOption(
                device.deviceId, 
                device.label,
                false,
                this.detectCameraType(device.label)
            );
            this.dropdown.appendChild(option);
        });
    }

    /**
     * Crée un élément option pour une caméra
     */
    createCameraOption(deviceId, label, isDefault = false, cameraType = 'default') {
        const option = document.createElement('div');
        option.className = 'camera-option';
        option.dataset.deviceId = deviceId;

        if (this.selectedDeviceId === deviceId) {
            option.classList.add('active');
        }

        const iconSvg = this.getCameraIconSvg(cameraType);
        option.innerHTML = `
            <span class="camera-icon">${iconSvg}</span>
            <span class="camera-label">${label}</span>
        `;

        option.addEventListener('click', () => {
            this.selectCamera(deviceId, label);
        });

        return option;
    }

    /**
     * Détecte le type de caméra pour afficher l'icône appropriée
     */
    detectCameraType(label) {
        const labelLower = label.toLowerCase();
        
        if (labelLower.includes('front') || labelLower.includes('user') || 
            labelLower.includes('selfie') || labelLower.includes('facetime')) {
            return 'front'; // Caméra frontale
        } else if (labelLower.includes('back') || labelLower.includes('rear') || 
                   labelLower.includes('environment')) {
            return 'back'; // Caméra arrière
        } else if (labelLower.includes('usb') || labelLower.includes('external')) {
            return 'external'; // Caméra externe
        }
        
        return 'default'; // Caméra générique
    }

    /**
     * Retourne l'icône SVG pour un type de caméra
     */
    getCameraIconSvg(cameraType) {
        const defaultCamera = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>';
        const frontCamera = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><circle cx="12" cy="10" r="3"></circle><line x1="12" y1="17" x2="12" y2="17"></line></svg>';
        const backCamera = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle><circle cx="12" cy="13" r="2"></circle></svg>';
        const externalCamera = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>';
        
        switch(cameraType) {
            case 'front': return frontCamera;
            case 'back': return backCamera;
            case 'external': return externalCamera;
            default: return defaultCamera;
        }
    }

    /**
     * Sélectionne une caméra
     */
    async selectCamera(deviceId, label) {
        try {
            //consolelog(`🎥 Changement vers: ${label}`);
            
            // Mettre à jour la sélection visuelle
            this.selectedDeviceId = deviceId;
            this.updateActiveOption();
            
            // Démarrer la nouvelle caméra
            await this.videoSourceManager.startWebcam(deviceId || undefined);
            
            // Fermer le dropdown
            this.closeDropdown();
            
            // Mettre à jour l'icône du bouton selon le type de caméra
            this.updateButtonIcon(label);
            
        } catch (error) {
            //consoleerror('Erreur lors du changement de caméra:', error);
        }
    }

    /**
     * Met à jour l'option active dans le dropdown
     */
    updateActiveOption() {
        // Retirer la classe active de toutes les options
        this.dropdown.querySelectorAll('.camera-option').forEach(option => {
            option.classList.remove('active');
        });

        // Ajouter la classe active à l'option sélectionnée
        const activeOption = this.dropdown.querySelector(`[data-device-id="${this.selectedDeviceId}"]`);
        if (activeOption) {
            activeOption.classList.add('active');
        }
    }

    /**
     * Met à jour l'icône du bouton selon le type de caméra
     */
    updateButtonIcon(label) {
        const cameraType = this.detectCameraType(label);
        this.button.textContent = cameraType;
    }

    /**
     * Bascule l'affichage du dropdown
     */
    toggleDropdown() {
        if (this.isDropdownOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    /**
     * Ouvre le dropdown
     */
    async openDropdown() {
        // Recharger les caméras au cas où elles auraient changé
        await this.loadAvailableCameras();
        
        this.dropdown.classList.add('show');
        this.isDropdownOpen = true;
        
        // Changer l'apparence du bouton
        this.button.style.background = 'rgba(0, 0, 0, 0.9)';
        this.button.style.borderColor = 'rgba(255, 255, 255, 0.6)';
    }

    /**
     * Ferme le dropdown
     */
    closeDropdown() {
        this.dropdown.classList.remove('show');
        this.isDropdownOpen = false;
        
        // Restaurer l'apparence du bouton
        this.button.style.background = 'rgba(0, 0, 0, 0.7)';
        this.button.style.borderColor = 'rgba(255, 255, 255, 0.3)';
    }

    /**
     * Met à jour les informations de caméra (appelé par le VideoSourceManager)
     */
    updateCameraInfo(facingMode) {
        // Mettre à jour l'icône selon le mode de caméra détecté
        if (facingMode === 'user') {
            this.button.innerHTML = this.getCameraIconSvg('front');
        } else if (facingMode === 'environment') {
            this.button.innerHTML = this.getCameraIconSvg('back');
        } else {
            this.button.innerHTML = this.getCameraIconSvg('default');
        }
    }

    /**
     * Rafraîchit la liste des caméras
     */
    async refresh() {
        await this.loadAvailableCameras();
    }
}
